import * as Dialog from '@radix-ui/react-dialog';

import { cn } from '~/chords/lib/cn';
import { renderSlot } from '~/chords/utils/renderSlot';

import { CONTENT_CLASS, OVERLAY_CLASS, SIDE_CLASS } from '../constants';
import type { SlideOutContentProps } from '../types';
import { getPanelWidth } from '../utils/getPanelWidth';
import { getSideTransform } from '../utils/getSideTransform';
import { SlideOutBody } from './SlideOutBody';
import { SlideOutFooter } from './SlideOutFooter';
import { SlideOutHeader } from './SlideOutHeader';

export function SlideOutContent({
  overlayRef,
  contentRef,
  panelWidth,
  side,
  children,
  ...props
}: SlideOutContentProps) {
  const width = getPanelWidth(panelWidth);
  const transform = getSideTransform({ side, width });

  return (
    <Dialog.Portal>
      <Dialog.Overlay
        ref={overlayRef}
        className={cn('fixed inset-0 z-20 bg-overlay', OVERLAY_CLASS.show)}
        role="none"
      />
      <Dialog.Content
        {...props}
        ref={contentRef}
        className={cn(
          'fixed inset-y-0 z-30',
          'flex max-w-full flex-col bg-level-02 text-default',
          'duration-300 ease-out',
          'shadow-xl drop-shadow-xl',
          SIDE_CLASS[side],
          CONTENT_CLASS.show,
          props.className,
        )}
        style={{ transform, width }}
        aria-describedby={undefined}
        // Don't close the SlideOut when interacting with a Toast notification.
        onInteractOutside={(event) => {
          if ((event.target as HTMLElement).closest('[data-sonner-toaster]')) {
            event.preventDefault();
          }
        }}
      >
        {renderSlot(SlideOutHeader, children)}
        {renderSlot(SlideOutBody, children)}
        {renderSlot(SlideOutFooter, children)}
      </Dialog.Content>
    </Dialog.Portal>
  );
}

SlideOutContent.displayName = 'SlideOut.Content';
